import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Coins, Wallet } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Card, EmptyState } from "@/components/ranger/PageHeader";
import { ConfigForm } from "@/components/ranger/Crud";
import { BTN_PRIMARY } from "@/pages/Servers";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtTime } from "@/lib/api";

const FIELDS = [
  { key: "currency_name", label: "Currency name" },
  { key: "starting_balance", label: "Starting balance", type: "number" },
  { key: "daily_reward", label: "Daily reward (0 = off)", type: "number" },
  { key: "daily_cooldown_hours", label: "Daily cooldown (hours)", type: "number" },
  { key: "delivery_enabled", label: "Auto-deliver items via RCON (inventory.giveto)", type: "switch", hint: "Unverified on RCE — test on staging first. When off, orders wait for staff delivery." },
  { key: "starter_kit_name", label: "Starter kit for new links", hint: "Exact kit name given free, once, when a player links (can be a disabled kit so it is not claimable). Leave blank to turn off. Offline players get it when they next join." },
  { key: "link_reward", label: "Coins for linking", type: "number", hint: "Given once when a player links their account (0 = off)." },
  { key: "referral_reward", label: "Referral bonus", type: "number", hint: "Paid once to a linked player named in a friend's /link invited_by (0 = off)." },
  { key: "starter_welcome_message", label: "In-game welcome after starter kit", hint: "Said in game chat once the starter kit is delivered. {gamertag} = player name. Leave blank to turn off." },
  { key: "streak_bonus", label: "Daily streak bonus per day", type: "number", hint: "Added on top of the daily reward for each consecutive day." },
  { key: "streak_max_days", label: "Streak bonus caps at day", type: "number" },
  { key: "bank_enabled", label: "Bank (deposit / withdraw)", type: "switch", hint: "Bank coins can't be gambled or spent until withdrawn." },
  { key: "pay_enabled", label: "Player-to-player payments", type: "switch" },
  { key: "weekly_champion_enabled", label: "Weekly Kill Champion", type: "switch", hint: "Every Sunday 6pm US Central: top PvP killer of the week is posted to Discord." },
  { key: "weekly_champion_reward", label: "Champion coin reward", type: "number", showIf: (v) => v.weekly_champion_enabled },
];
const inputCls = "border-[#1E2D4A] bg-[#050811] font-mono";

const Adjust = ({ cid, onDone, preset }) => {
  const [f, setF] = useState({ discord_id: "", amount: "", reason: "" });
  useEffect(() => { if (preset) setF((x) => ({ ...x, discord_id: preset })); }, [preset]);
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post(`/communities/${cid}/wallets/adjust`, { ...f, amount: Number(f.amount) }); toast.success(`New balance ${data.balance}`); setF({ discord_id: "", amount: "", reason: "" }); onDone(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="mb-5 grid gap-2 sm:grid-cols-[1.3fr_0.7fr_1.5fr_auto]">
      <Input data-testid="adjust-discord-id" required placeholder="Discord ID" value={f.discord_id} onChange={(e) => setF({ ...f, discord_id: e.target.value })} className={inputCls} />
      <Input data-testid="adjust-amount" required type="number" placeholder="+/- amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={inputCls} />
      <Input data-testid="adjust-reason" required placeholder="Reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className="border-[#1E2D4A] bg-[#050811]" />
      <button data-testid="adjust-submit" className={BTN_PRIMARY}>Apply</button>
    </form>
  );
};

const LedgerTable = ({ rows }) => (
  <Table data-testid="ledger-table">
    <TableHeader><TableRow className="border-[#1E2D4A]">{["Time", "Discord ID", "Change", "Balance", "Type", "Note", "By"].map((h) => <TableHead key={h} className="rg-label">{h}</TableHead>)}</TableRow></TableHeader>
    <TableBody>{rows.map((r) => (
      <TableRow key={r.id} className="border-[#1E2D4A]/60 font-mono text-xs">
        <TableCell className="whitespace-nowrap text-slate-400">{fmtTime(r.created_at)}</TableCell><TableCell className="text-slate-400">{r.discord_id}</TableCell>
        <TableCell className={r.amount < 0 ? "text-red-400" : "text-emerald-400"}>{r.amount > 0 ? `+${r.amount}` : r.amount}</TableCell>
        <TableCell className="text-slate-200">{r.balance_after}</TableCell><TableCell className="text-slate-300">{r.kind}</TableCell>
        <TableCell className="text-slate-400">{r.note}</TableCell><TableCell className="text-slate-400">{r.actor}</TableCell>
      </TableRow>
    ))}</TableBody>
  </Table>
);

export default function Economy() {
  const { active, can } = useAuth();
  const [wallets, setWallets] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [pick, setPick] = useState(null);
  const load = useCallback(() => {
    api.get(`/communities/${active.id}/wallets`).then((r) => setWallets(r.data)).catch(() => {});
    api.get(`/communities/${active.id}/ledger`).then((r) => setLedger(r.data)).catch(() => {});
  }, [active.id]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <PageHeader eyebrow="Community" title="Economy" description="Every balance change is a ledger entry. Balances can never go negative." />
      <ConfigForm cid={active.id} field="economy" fields={FIELDS} title="Economy settings" icon={Coins} />
      <Card title="Wallets" icon={Wallet} testId="wallets-card" className="mb-5">
        {can("admin") && <Adjust cid={active.id} onDone={load} preset={pick} />}
        {wallets.length === 0 ? <EmptyState title="No wallets yet" testId="wallets-empty">Wallets are created when a linked player claims a reward or makes a purchase.</EmptyState> : (
          <ul data-testid="wallets-list" className="divide-y divide-[#1E2D4A]">{wallets.map((w) => (
            <li key={w.id} className="flex items-center justify-between py-2 text-sm">
              <button onClick={() => setPick(w.discord_id)} className="text-left text-slate-200 hover:text-white">{w.gamertag || "Unlinked"} <span className="font-mono text-xs text-slate-500">{w.discord_id}</span></button>
              <span className="font-mono text-slate-100">{w.balance}{w.bank ? <span className="ml-3 text-xs text-slate-500">bank {w.bank}</span> : null}</span>
            </li>
          ))}</ul>
        )}
      </Card>
      <Card title="Ledger"><div className="overflow-x-auto">{ledger.length ? <LedgerTable rows={ledger} /> : <p className="text-sm text-slate-500">No transactions yet.</p>}</div></Card>
    </div>
  );
}
